// recaptcha.ts
// The reCAPTCHA site key differs per environment and is served by the backend
// (/api/AppInfo/versions -> recaptchaSiteKey). The app registers a loader for it via
// setRecaptchaSiteKeyLoader; loading/execution wait for it so the correct key is used.
//
// The key is a policy-based challenge key: reCAPTCHA scores the request, compares it
// against the threshold configured on the key, and shows a CAPTCHA challenge when the
// score falls short. Such keys must be driven through a rendered widget —
// execute(siteKey, { action }) is the score-based entry point and cannot present a
// challenge, so it silently yields a token the backend then rejects (which is what
// broke submissions in Edge, where Tracking Prevention depresses the score).
//
// Neither the key nor the script is cached once it has failed: both fail mostly for
// client-side reasons (flaky mobile data, an ad blocker or DNS filter that can be turned
// off), and production logs showed customers clicking Submit up to 13 times against a
// failure that had been memoised on page load and could never clear without a reload.

/**
 * reCAPTCHA could not be set up in this browser: the site key or Google's script did not
 * load. Unlike a failed challenge, the customer can fix this (ad blocker, network), so pages
 * show a dedicated message for it.
 */
export class RecaptchaUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RecaptchaUnavailableError';
  }
}

/** What the customer is told when verification could not complete. */
export function describeRecaptchaFailure(error: unknown): string {
  return error instanceof RecaptchaUnavailableError
    ? "We couldn't load the security check. It may be blocked by an ad blocker, a privacy extension or your network. Please allow it for this page or try another connection, then submit again."
    : 'Unable to complete verification. Please try again.';
}

type SiteKeyLoader = () => Promise<string | null | undefined>;

let siteKeyLoader: SiteKeyLoader | null = null;
let markLoaderReady!: () => void;
// Pages can ask for reCAPTCHA before the app has registered the loader.
const loaderReady = new Promise<void>((resolve) => {
  markLoaderReady = resolve;
});

export function setRecaptchaSiteKeyLoader(loader: SiteKeyLoader): void {
  siteKeyLoader = loader;
  markLoaderReady();
}

let siteKeyPromise: Promise<string> | null = null;

function getSiteKey(): Promise<string> {
  if (siteKeyPromise) return siteKeyPromise;

  const attempt = loaderReady
    .then(() => siteKeyLoader!())
    .then(
      (key) => {
        if (!key) {
          throw new RecaptchaUnavailableError('reCAPTCHA site key missing in AppInfo response');
        }
        return key;
      },
      (error: unknown) => {
        throw new RecaptchaUnavailableError(
          `AppInfo versions request failed: ${error instanceof Error ? error.message : String(error)}`,
        );
      },
    );

  siteKeyPromise = attempt;
  // A failed attempt is dropped so the next submit asks again.
  attempt.catch(() => {
    if (siteKeyPromise === attempt) siteKeyPromise = null;
  });

  return attempt;
}

let loadPromise: Promise<any> | null = null;

/**
 * Resolves with the Enterprise API itself rather than leaving callers to read it off
 * `window`: the page can load the classic reCAPTCHA after us, which replaces
 * `window.grecaptcha` with an object that has no `enterprise`, and the submit then failed
 * with "Cannot read properties of undefined (reading 'reset')".
 */
function loadRecaptchaEnterprise(): Promise<any> {
  if (loadPromise) return loadPromise;

  const attempt = getSiteKey().then(
    (siteKey) =>
      new Promise<any>((resolve, reject) => {
        const loaded = (window as any).grecaptcha?.enterprise;
        if (loaded) {
          resolve(loaded);
          return;
        }

        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
        script.async = true;
        script.onload = () => {
          const enterprise = (window as any).grecaptcha.enterprise;
          enterprise.ready(() => resolve(enterprise));
        };
        script.onerror = () => {
          // Removed so a retry appends a fresh tag instead of leaving dead ones behind.
          script.remove();
          reject(new RecaptchaUnavailableError('Failed to load reCAPTCHA'));
        };
        document.head.appendChild(script);
      }),
  );

  loadPromise = attempt;
  // A failed load is dropped so the next submit retries it.
  attempt.catch(() => {
    if (loadPromise === attempt) loadPromise = null;
  });

  return attempt;
}

/**
 * Raised on the earlier attempt when a second verification starts before the first
 * finished — the user dismissing a challenge fires no callback, so the only way out
 * is to let a fresh click supersede the stale attempt. Callers should ignore it.
 */
export class RecaptchaSupersededError extends Error {
  constructor() {
    super('reCAPTCHA verification superseded by a newer request');
    this.name = 'RecaptchaSupersededError';
  }
}

interface PendingExecution {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}

interface ActionWidget {
  id: number;
  pending: PendingExecution | null;
}

// The action is fixed when a widget is rendered, so each action gets its own widget.
const widgets = new Map<string, ActionWidget>();

function getWidget(action: string, siteKey: string, grecaptcha: any): ActionWidget {
  const existing = widgets.get(action);
  if (existing) return existing;

  const widget: ActionWidget = { id: -1, pending: null };

  const settle = (handle: (pending: PendingExecution) => void): void => {
    const { pending } = widget;
    if (!pending) return;
    widget.pending = null;
    handle(pending);
  };

  const container = document.createElement('div');
  document.body.appendChild(container);

  widget.id = grecaptcha.render(container, {
    sitekey: siteKey,
    action,
    size: 'invisible',
    badge: 'bottomright',
    callback: (token: string) => settle((pending) => pending.resolve(token)),
    'error-callback': () => settle((pending) => pending.reject(new Error('reCAPTCHA challenge failed'))),
    'expired-callback': () => settle((pending) => pending.reject(new Error('reCAPTCHA challenge expired'))),
  });

  widgets.set(action, widget);
  return widget;
}

/**
 * Loads reCAPTCHA and renders the widget for `action` up front, so the badge is on the
 * page from the start rather than appearing on the first submit — Google requires the
 * badge (or an equivalent attribution) to be visible wherever reCAPTCHA is used.
 */
export async function prepareRecaptcha(action: string): Promise<void> {
  const grecaptcha = await loadRecaptchaEnterprise();
  const siteKey = await getSiteKey();
  getWidget(action, siteKey, grecaptcha);
}

export async function executeRecaptcha(action: string): Promise<string> {
  const grecaptcha = await loadRecaptchaEnterprise();
  const siteKey = await getSiteKey();
  const widget = getWidget(action, siteKey, grecaptcha);

  settlePendingAsSuperseded(widget);
  // Without a reset the widget keeps the previous response and execute() is a no-op.
  grecaptcha.reset(widget.id);

  return new Promise<string>((resolve, reject) => {
    widget.pending = { resolve, reject };
    grecaptcha.execute(widget.id);
  });
}

function settlePendingAsSuperseded(widget: ActionWidget): void {
  const { pending } = widget;
  if (!pending) return;
  widget.pending = null;
  pending.reject(new RecaptchaSupersededError());
}
